import { useEffect, useState } from "react";
import SectionEyebrow from "./SoftwarePageEyebrow";

const CINEMATIC_CARDS = [
  {
    kicker: "Before and after",
    icon: "compare",
    accent: "#0ea5e9",
    title: "Compare the original with a refined direction.",
    desc: "Move from a plain room scan to a redesigned interior with clear visual contrast.",
    image: "/assets/app/cinematic/cinematic-2.jpg",
    imageFit: "contain",
    imagePosition: "center center",
    imageVariant: "portrait",
  },
  {
    kicker: "Design choices",
    icon: "palette",
    accent: "#10b981",
    title: "Explore styles, palettes, and room intent.",
    desc: "Guide the model with decisions that feel personal, practical, and visually coherent.",
    image: "/assets/app/cinematic/cinematic-3.jpg",
    imageFit: "contain",
    imagePosition: "center center",
    imageVariant: "wide",
  },
  {
    kicker: "Design brief",
    icon: "brief",
    accent: "#8b5cf6",
    title: "Start with what matters most.",
    desc: "Guide the design with priorities that keep every recommendation personal, useful, and grounded.",
    image: "/assets/app/cinematic/cinematic-1.jpg",
    imageFit: "contain",
    imagePosition: "center center",
    imageVariant: "portrait",
  },
  {
    kicker: "Spatial preview",
    icon: "spatial",
    accent: "#f97316",
    title: "Step inside the finished room.",
    desc: "Review the redesigned space in a horizontal, room-scale view built for spatial exploration.",
    image: "/assets/app/cinematic/cinematic-4.jpg",
    imageFit: "contain",
    imagePosition: "center center",
    imageVariant: "horizontal",
  },
];

const FeatureKickerIcon = ({ type }) => {
  const paths = {
    compare: (
      <>
        <rect x="4" y="5" width="7" height="14" rx="2" />
        <rect x="13" y="5" width="7" height="14" rx="2" />
        <path d="M11 9h2" />
        <path d="M11 15h2" />
      </>
    ),
    palette: (
      <>
        <path d="M12 4.5c-4.1 0-7.5 2.9-7.5 6.6 0 3.1 2.4 5.8 5.6 6.4.9.2 1.5-.4 1.5-1.1 0-.5-.3-.9-.3-1.4 0-.8.7-1.3 1.5-1.3H14c3.2 0 5.5-1.7 5.5-4.2 0-2.8-3.3-5-7.5-5Z" />
        <circle cx="8.5" cy="10" r=".8" fill="currentColor" stroke="none" />
        <circle cx="11.5" cy="8.3" r=".8" fill="currentColor" stroke="none" />
        <circle cx="14.8" cy="9.5" r=".8" fill="currentColor" stroke="none" />
      </>
    ),
    brief: (
      <>
        <rect x="5" y="6.5" width="14" height="12" rx="2.4" />
        <path d="M9.5 6.5V5.2c0-.8.6-1.2 1.3-1.2h2.4c.8 0 1.3.5 1.3 1.2v1.3" />
        <path d="M8.5 11h7" />
        <path d="M8.5 14.5h4" />
      </>
    ),
    spatial: (
      <>
        <path d="M12 4.5 18.5 8v8L12 19.5 5.5 16V8L12 4.5Z" />
        <path d="m5.8 8.2 6.2 3.4 6.2-3.4" />
        <path d="M12 11.6v7.5" />
      </>
    ),
  };

  return (
    <svg className="app-screen-kicker-icon" viewBox="0 0 24 24" aria-hidden="true">
      {paths[type] || paths.brief}
    </svg>
  );
};

// Isolated from AppPlatformPage on purpose: this carousel auto-advances on a
// timer, and keeping its state here (instead of on the page component) means
// the 4.2s tick only re-renders this small subtree instead of the whole
// page — including the 90+ marquee cards further down — every 4.2 seconds.
export default function AppScreensCarousel() {
  const [carouselPosition, setCarouselPosition] = useState(0);
  const [carouselTransitionEnabled, setCarouselTransitionEnabled] = useState(true);
  const carouselSlides = [...CINEMATIC_CARDS, CINEMATIC_CARDS[0]];
  const activeShot = carouselPosition % CINEMATIC_CARDS.length;

  const handleCarouselTransitionEnd = (event) => {
    if (event.target !== event.currentTarget || carouselPosition !== CINEMATIC_CARDS.length) {
      return;
    }

    setCarouselTransitionEnabled(false);
    setCarouselPosition(0);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setCarouselTransitionEnabled(true));
    });
  };

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCarouselPosition((current) => {
        if (current >= CINEMATIC_CARDS.length) {
          return current;
        }

        return current + 1;
      });
    }, 4200);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <section className="section app-screens-section" id="app-screens">
      <div className="container">
        <span id="demo" aria-hidden="true" />
        <SectionEyebrow icon="appFeatures">Features</SectionEyebrow>
        <h2 className="reveal app-screens-title">See the redesign flow at a glance.</h2>
        <p className="reveal reveal-d1 app-screens-subhead">A clean, card-based gallery of real app screens from concept to final space.</p>

        <div className="reveal reveal-d2 app-screens-carousel" aria-label="Ateno Studio feature screenshots">
          <div className="app-screens-carousel-window">
            <div
              className="app-screens-carousel-track"
              role="list"
              onTransitionEnd={handleCarouselTransitionEnd}
              style={{
                transform: `translate3d(-${carouselPosition * 100}%, 0, 0)`,
                transition: carouselTransitionEnabled ? undefined : "none",
              }}
            >
              {carouselSlides.map((card, index) => (
                <article
                  key={`${card.kicker}-${index}`}
                  role="listitem"
                  className={`app-screen-card app-screen-card-${card.imageVariant} ${carouselPosition === index ? "is-active" : ""}`}
                  aria-hidden={carouselPosition !== index}
                >
                  <div className="app-screen-media-wrap">
                    <img
                      src={card.image}
                      alt={card.title}
                      className={`app-screen-media app-screen-media-${card.imageVariant}`}
                      style={{ objectFit: card.imageFit, objectPosition: card.imagePosition }}
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                    />
                  </div>
                  <div className="app-screen-copy">
                    <div className="app-screen-kicker" style={{ "--kicker-accent": card.accent }}>
                      <span className="app-screen-kicker-mark">
                        <FeatureKickerIcon type={card.icon} />
                      </span>
                      <span>{card.kicker}</span>
                    </div>
                    <h3>{card.title}</h3>
                    <p>{card.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
        <div className="reveal reveal-d3 app-screens-carousel-dots" role="tablist" aria-label="Select feature screenshot">
          {CINEMATIC_CARDS.map((card, index) => (
            <button
              key={card.kicker}
              type="button"
              className={`app-screens-carousel-dot ${activeShot === index ? "is-active" : ""}`}
              onClick={() => {
                setCarouselTransitionEnabled(true);
                setCarouselPosition(index);
              }}
              aria-label={`Show ${card.kicker}`}
              aria-selected={activeShot === index}
              role="tab"
            >
              <span>{card.kicker}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
